import React, { useCallback, useEffect, useState } from "react";
import { fmtDate } from "@/lib/dates";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, TrendingUp } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { apiErr } from "@/lib/api";
import { ragOf, RAG_STROKE } from "@/lib/rag";

const CHART_MARGIN = { top: 5, right: 12, left: -18, bottom: 0 };
const AXIS_TICK = { fontSize: 11, fill: "#8a8178" };
const Y_DOMAIN = [0, 100];
const TOOLTIP_STYLE = { borderRadius: 12, border: "1px solid #e7e0d6", fontSize: 12 };
const tooltipFmt = (v) => [`${v}%`, "Score"];
const DOT_NAVY = { r: 3, fill: "#1E3A5F" };
const ACTIVE_DOT = { r: 5 };
const toSeries = (hist) => (hist || []).map((h) => ({ label: fmtDate(h.date).slice(0, 5), score: h.score }));

export default function MonthlyCheck() {
  const [tpl, setTpl] = useState([]);
  const [items, setItems] = useState({});
  const [actions, setActions] = useState({});
  const [lastCheck, setLastCheck] = useState(null);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/compliance/monthly-check").then((r) => {
    setTpl(r.data.template || []); setItems(r.data.items || {}); setActions(r.data.actions || {}); setLastCheck(r.data.last_check_at); setHistory(r.data.history || []);
  }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const n = tpl.filter((_, i) => items[i] !== "na").length;
  const yes = tpl.reduce((a, _, i) => a + (items[i] === "yes" ? 1 : 0), 0);
  const pct = n ? Math.round(yes / n * 100) : 0;
  const rag = ragOf(pct);
  const fmt = (iso) => (iso ? fmtDate(iso) : null);

  const submit = async () => {
    setSaving(true);
    try { const { data } = await api.post("/compliance/monthly-check", { items, actions }); setLastCheck(data.last_check_at); toast.success("Monthly check submitted"); load(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div data-testid="monthly-overall" className="rounded-2xl bg-brand text-white p-6 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-5">
            <div className="relative h-16 w-16 grid place-items-center">
              <svg className="h-16 w-16 -rotate-90" viewBox="0 0 36 36">
                <circle cx="18" cy="18" r="15.9" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="3" />
                <circle cx="18" cy="18" r="15.9" fill="none" stroke={RAG_STROKE[rag]} strokeWidth="3" strokeDasharray={`${pct}, 100`} strokeLinecap="round" />
              </svg>
              <span className="absolute text-lg font-bold font-heading">{pct}%</span>
            </div>
            <div>
              <div className="label-caps text-white/60">Monthly Oral Care Check</div>
              <div className="text-lg font-bold mt-1">Quick management assurance</div>
              <div className="text-xs text-white/60 mt-1">Last completed: {fmt(lastCheck) || "Not yet completed"} · {yes}/{n} on track</div>
            </div>
          </div>
          <Button data-testid="monthly-submit-btn" onClick={submit} disabled={saving} className="rounded-full bg-coral hover:bg-coral-hover text-white disabled:opacity-60">
            <CheckCircle2 className="h-4 w-4 mr-2" />{saving ? "Submitting…" : "Submit monthly check"}
          </Button>
        </div>
      </div>

      <div className="bg-cream-card border border-cream-deep rounded-2xl p-5 space-y-2">
        <p className="text-xs text-muted-foreground mb-2">A quick monthly review of the things most likely to slip between quarterly assessments.</p>
        {tpl.map((q, i) => (
          <div key={q} className="flex flex-wrap items-center gap-3 py-2 border-b border-cream last:border-0">
            <span className="flex-1 min-w-[220px] text-sm">{q}</span>
            <div className="flex gap-1">
              <button data-testid={`monthly-${i}-yes`} onClick={() => setItems({ ...items, [i]: "yes" })}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${items[i] === "yes" ? "bg-rag-green text-white" : "bg-cream text-muted-foreground hover:bg-rag-green-bg"}`}>Yes</button>
              <button data-testid={`monthly-${i}-no`} onClick={() => setItems({ ...items, [i]: "no" })}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${items[i] === "no" ? "bg-rag-red text-white" : "bg-cream text-muted-foreground hover:bg-rag-red-bg"}`}>No</button>
              {/denture/i.test(q) && <button data-testid={`monthly-${i}-na`} title="Not applicable — no residents with dentures" onClick={() => setItems({ ...items, [i]: "na" })}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${items[i] === "na" ? "bg-brand text-white" : "bg-cream text-muted-foreground hover:bg-brand-light"}`}>N/A</button>}
            </div>
            {items[i] === "no" && (
              <Input data-testid={`monthly-action-${i}`} placeholder="Action…" defaultValue={actions[i] || ""}
                onBlur={(e) => setActions({ ...actions, [i]: e.target.value })} className="h-8 text-xs w-full md:w-56" />
            )}
          </div>
        ))}
      </div>

      {history.length >= 2 && (
        <div data-testid="monthly-trend" className="rounded-2xl bg-cream-card border border-cream-deep p-5 mt-6">
          <div className="flex items-center gap-2 mb-4"><TrendingUp className="h-4 w-4 text-brand" /><h4 className="font-heading font-bold text-brand text-sm">Monthly check trend</h4></div>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={toSeries(history)} margin={CHART_MARGIN}>
              <defs><linearGradient id="monthlyGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#F26B50" stopOpacity={0.35} /><stop offset="100%" stopColor="#F26B50" stopOpacity={0.02} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e7e0d6" vertical={false} />
              <XAxis dataKey="label" tick={AXIS_TICK} axisLine={false} tickLine={false} />
              <YAxis domain={Y_DOMAIN} tick={AXIS_TICK} axisLine={false} tickLine={false} width={40} />
              <Tooltip formatter={tooltipFmt} contentStyle={TOOLTIP_STYLE} />
              <Area type="monotone" dataKey="score" stroke="#F26B50" strokeWidth={2.5} fill="url(#monthlyGrad)" dot={DOT_NAVY} activeDot={ACTIVE_DOT} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
